/**
 * 字典项资源配图上传：选择图片 → 1:1 裁剪 → 按规格生成 1x/2x 并上传，得到 url_1x / url_2x 供 extra.assets 使用。
 * 规范：岗位标签 1x 88×88px，2x 176×176px；先上传再存 URL。
 */
import ImgCrop from 'antd-img-crop';
import { Upload, Image, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import React, { useCallback, useState } from 'react';
import { useIntl } from '@umijs/max';
import { generateUploadUrl, uploadFile } from '@/services/admin/file';

export interface AssetImageSpec {
  width1x: number;
  height1x: number;
  width2x: number;
  height2x: number;
}

/** extra.assets 中单条图片资源 */
export interface ExtraAssetImage {
  type: 'image';
  url_2x: string;
  url_1x?: string;
}

export interface AssetImageUploadProps {
  /** 当前值（来自 extra.assets[0]） */
  value?: ExtraAssetImage | null;
  /** 值变化回调 */
  onChange?: (asset: ExtraAssetImage | null) => void;
  /** 1x/2x 尺寸规格 */
  spec: AssetImageSpec;
  /** 是否禁用 */
  disabled?: boolean;
  /** 业务类型，上传用 */
  businessType?: 'image';
  /** 占位提示 */
  placeholder?: string;
  /** 上传操作框与预览图的展示尺寸；不改变最终生成的 1x/2x 图片规格 */
  displaySize?: number;
}

/** 获取图片文件的宽高（裁剪后实际像素尺寸） */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = document.createElement('img');
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
}

/**
 * 将图片文件缩放到指定宽高并转为 Blob。
 * 使用传入的 mimeType 导出，PNG 时保留透明通道（不填充画布）；JPEG 时按 quality 压缩。
 */
function resizeImageToBlob(
  file: File,
  width: number,
  height: number,
  mimeType: string = 'image/jpeg',
  quality: number = 0.92,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = document.createElement('img');
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2d not available'));
        return;
      }
      // 不 fillRect，保留 PNG 透明；导出格式由 mimeType 决定
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        mimeType,
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
}

/** 上传 Blob 到 OSS，返回 file_url */
async function uploadBlobAsImage(
  blob: Blob,
  filename: string,
  customPrefix?: string | null,
): Promise<string> {
  const file = new File([blob], filename, { type: blob.type });
  try {
    const response = await generateUploadUrl({
      business_type: 'image',
      filename,
      custom_prefix: customPrefix || undefined,
    });
    if (response.code !== 0 || !response.data) {
      throw new Error(response.message || '获取上传凭证失败');
    }
    const { upload_url, form_data } = response.data;
    const formData = new FormData();
    Object.keys(form_data).forEach((key) => {
      formData.append(key, form_data[key]);
    });
    formData.append('file', file);
    const uploadResponse = await fetch(upload_url, { method: 'POST', body: formData });
    if (!uploadResponse.ok) {
      throw new Error(`上传失败: ${uploadResponse.statusText}`);
    }
    return response.data.file_url;
  } catch (e: any) {
    console.warn('直传失败，降级服务端中转:', e);
    const res = await uploadFile(file, {
      business_type: 'image',
      custom_prefix: customPrefix || undefined,
    });
    if (res.code === 0 && res.data?.file_url) return res.data.file_url;
    throw new Error(res.message || '上传失败');
  }
}

const AssetImageUpload: React.FC<AssetImageUploadProps> = ({
  value,
  onChange,
  spec,
  disabled = false,
  businessType = 'image',
  placeholder,
  displaySize,
}) => {
  const intl = useIntl();
  const [uploading, setUploading] = useState(false);
  const previewUrl = value?.url_2x || value?.url_1x || '';
  const renderedSize = displaySize ?? spec.width2x;

  const handleUpload = useCallback(
    async (file: RcFile) => {
      setUploading(true);
      try {
        const { width, height } = await getImageDimensions(file);
        const minSide = Math.min(width, height);
        if (minSide < spec.width2x) {
          message.warning(
            intl.formatMessage(
              { id: '页面.配置.岗位标签.配图尺寸过小' },
              { min: spec.width2x, current: minSide },
            ),
          );
          setUploading(false);
          return;
        }
        const mimeType = file.type || 'image/jpeg';
        const [blob2x, blob1x] = await Promise.all([
          resizeImageToBlob(file, spec.width2x, spec.height2x, mimeType),
          resizeImageToBlob(file, spec.width1x, spec.height1x, mimeType),
        ]);
        const ext = mimeType === 'image/png' ? 'png' : 'jpg';
        const [url_2x, url_1x] = await Promise.all([
          uploadBlobAsImage(blob2x, `2x.${ext}`),
          uploadBlobAsImage(blob1x, `1x.${ext}`),
        ]);
        onChange?.({ type: 'image', url_2x, url_1x });
        message.success(intl.formatMessage({ id: '通用.上传成功' }));
      } catch (err: any) {
        message.error(err?.message || intl.formatMessage({ id: '通用.上传失败' }));
      } finally {
        setUploading(false);
      }
      return Promise.reject(); // 阻止 Upload 默认行为，我们自行处理
    },
    [spec, onChange, intl],
  );

  const handleRemove = useCallback(() => {
    onChange?.(null);
  }, [onChange]);

  return (
    <div className="asset-image-upload">
      {previewUrl ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Image
              src={previewUrl}
              alt="配图"
              width={renderedSize}
              height={renderedSize}
              style={{ objectFit: 'cover', display: 'block', borderRadius: 4 }}
            />
            {!disabled && (
              <div style={{ marginTop: 8 }}>
                <ImgCrop aspect={1} quality={1} zoom rotate fillColor="transparent" modalTitle={placeholder}>
                  <Upload
                    showUploadList={false}
                    accept="image/jpeg,image/png,image/jpg"
                    beforeUpload={(f) => {
                      handleUpload(f as RcFile);
                      return false;
                    }}
                    disabled={uploading}
                  >
                    <span style={{ cursor: uploading ? 'not-allowed' : 'pointer', color: '#1677ff' }}>
                      {uploading ? intl.formatMessage({ id: '通用.上传中' }) : intl.formatMessage({ id: '通用.更换图片' })}
                    </span>
                  </Upload>
                </ImgCrop>
                <span style={{ marginLeft: 8, cursor: disabled ? 'default' : 'pointer', color: 'red' }} onClick={handleRemove}>
                  {intl.formatMessage({ id: '通用.删除' })}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <ImgCrop aspect={1} quality={1} zoom rotate fillColor="transparent" modalTitle={placeholder}>
          <Upload
            showUploadList={false}
            accept="image/jpeg,image/png,image/jpg"
            beforeUpload={(f) => {
              handleUpload(f as RcFile);
              return false;
            }}
            disabled={disabled || uploading}
          >
            <div
              style={{
                width: renderedSize,
                height: renderedSize,
                border: '1px dashed #d9d9d9',
                borderRadius: 4,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8,
                color: '#999',
                cursor: disabled || uploading ? 'not-allowed' : 'pointer',
                background: '#fafafa',
                lineHeight: 1.5,
                textAlign: 'center',
              }}
            >
              {uploading ? intl.formatMessage({ id: '通用.上传中' }) : (placeholder || intl.formatMessage({ id: '通用.选择图片' }))}
            </div>
          </Upload>
        </ImgCrop>
      )}
    </div>
  );
};

export default AssetImageUpload;
